import { toast } from "@redotech/redo-web/alert";
import type { KeyboardShortcut } from "@redotech/redo-web/keyboard-shortcuts/keyboard-shortcut";
import {
  useKeyboardShortcuts,
  type ShortcutBinding,
} from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { useSyncExternalStore } from "react";
import { useIsRedoAdmin } from "./permissions-context";

const STORAGE_KEY = "redo.employee-tools";
const CHANGED_EVENT = "redo:employee-tools-changed";

/** Falls back to this when storage is unavailable, so the switch still applies to the tab. */
let inMemoryShown = false;

function readShown(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return inMemoryShown;
  }
}

function subscribeToShown(onChange: () => void) {
  window.addEventListener(CHANGED_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGED_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** Flips the switch for this browser and returns where it landed. */
export function toggleRedoEmployeeTools(): boolean {
  const shown = !readShown();
  inMemoryShown = shown;
  try {
    if (shown) {
      localStorage.setItem(STORAGE_KEY, "true");
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Private mode or quota exhausted; the in-memory value still applies.
  }
  window.dispatchEvent(new Event(CHANGED_EVENT));
  return shown;
}

/**
 * Whether to show Redo-employee-only tools, such as links into admin. Off
 * until switched on, so an employee's demo looks like the merchant's app.
 */
export function useRedoEmployeeTools(): boolean {
  const isRedoAdmin = useIsRedoAdmin();
  const shown = useSyncExternalStore(subscribeToShown, readShown);
  return isRedoAdmin && shown;
}

/** Kept out of the shortcut registry, which feeds the list merchants see. */
const TOGGLE_SHORTCUT: KeyboardShortcut = {
  code: "KeyE",
  keyLabel: "E",
  modifiers: ["alt", "shift"],
};

const TOGGLE_BINDINGS: readonly ShortcutBinding[] = [
  {
    shortcut: TOGGLE_SHORTCUT,
    handler: () => {
      const shown = toggleRedoEmployeeTools();
      toast(shown ? "Redo employee tools on" : "Redo employee tools off", {
        variant: "info",
        dismissPrevious: true,
      });
    },
  },
];

/** Lets a Redo employee flip the switch with Alt+Shift+E. Mount it once. */
export function useRedoEmployeeToolsShortcut(): void {
  useKeyboardShortcuts({
    bindings: TOGGLE_BINDINGS,
    scope: "global",
    enabled: useIsRedoAdmin(),
  });
}
